import React from 'react';

import './ScoresWindow.css';

// ScoresWindow — Desktop preset's standalone Scores floating window.
//
// One horizontal progress bar per seated player against the 24-Snohe
// Singularity threshold. Segment math mirrors the legacy
// PlayerScoreBar (hotel-interface-score-bars region inside
// HotelInterface) so the visual contract carries over without the
// react-bootstrap ProgressBar dependency. Phone Companion, Tablet,
// and TV Observer presets keep the legacy embedded ScoreBars and
// are unaffected by this component or its stylesheet.
//
// Sizing: assumes the parent is a FloatingWindow body, which provides
// a definite height via flex:1 + min-height:0 + position:relative.
// The root pins to that body via absolute insets (same trick as
// CompetitorsWindow) so the row stack always fills the available
// height, never collapses to zero and never overflows the title bar.

const FACTION_SLUGS = [
    null, 'deep', 'stem', 'hex', 'dread', 'honored', 'seed',
];

const MAX_SCORE = 24;
const THRESHOLD = 20;

// Compute the four bar segments for one player, left-to-right.
//
// Segment kinds:
//   'solid'    — Snohe earned in previous rounds, full faction color.
//   'striped'  — Snohe earned this round, striped + animated.
//   'faded'    — gap from current total up to the 20-Snohe threshold,
//                rendered in the faded-RGBA faction color.
//   'danger'   — the 20 → 24 segment, only nonzero when current score
//                has reached 20 (the "you must score a 4+ source to
//                win" zone from the manual).
//
// `units` is the segment's width on the 0-24 scale and is passed
// straight through to CSS flex-grow so the four segments distribute
// proportionally inside their parent bar. The four units always sum
// to 24 — every player's bar is fully populated, just with different
// proportions of the four kinds.
export function computeSegments(currentScore, currentRoundPoints) {
    const round = Math.max(0, currentRoundPoints || 0);
    const total = Math.max(0, Math.min(MAX_SCORE, currentScore || 0));
    const prior = Math.max(0, total - round);
    const faded = Math.max(0, THRESHOLD - (prior + round));
    const danger = Math.max(
        0, MAX_SCORE - (prior + round + faded));
    return [
        { kind: 'solid',   units: prior  },
        { kind: 'striped', units: round  },
        { kind: 'faded',   units: faded  },
        { kind: 'danger',  units: danger },
    ];
}

function PlayerRow({ gamePlayerData, isActive, isLeader }) {
    const slug = FACTION_SLUGS[gamePlayerData.faction] || 'unaligned';
    const segments = computeSegments(
        gamePlayerData.current_score,
        gamePlayerData.current_round_points);
    const score = Math.max(0, gamePlayerData.current_score || 0);
    const classes = [
        'scores-window__row',
        isActive ? 'scores-window__row--active' : '',
        isLeader ? 'scores-window__row--leader' : '',
    ].filter(Boolean).join(' ');
    return (
        <div className={classes}
             data-faction={slug}
             data-testid={'scores-window-row'}>
            <div className={'scores-window__label'}>
                <span className={'scores-window__name'}>
                    {gamePlayerData.name}
                </span>
                <span className={'scores-window__score'}
                      data-testid={'scores-window-score'}>
                    {score}
                </span>
            </div>
            <div className={'scores-window__bar'}>
                {segments.map((seg, i) => (
                    seg.units > 0 ? (
                        <div key={i}
                             className={'scores-window__seg '
                                 + 'scores-window__seg--' + seg.kind}
                             data-faction={slug}
                             data-kind={seg.kind}
                             style={{ flexGrow: seg.units }}/>
                    ) : null
                ))}
            </div>
        </div>
    );
}

function ScoresWindow(props) {
    const gameData = props.gameData;
    if (!gameData) return null;
    const seated = Array.isArray(gameData.gameplayer_set)
        ? gameData.gameplayer_set.filter(
            (gp) => gp.faction !== null && gp.faction !== undefined)
        : [];
    if (seated.length === 0) return null;

    // Leader = highest current score above zero. Zero-zero pre-game
    // doesn't get a leader badge so the boldface doesn't tag every
    // row before scoring starts. Ties are co-leaders.
    const leaderScore = seated.reduce(
        (m, gp) => Math.max(m, gp.current_score || 0), 0);

    return (
        <div className={'scores-window'}
             data-testid={'scores-window'}>
            {seated.map((gp) => (
                <PlayerRow
                    key={gp.id}
                    gamePlayerData={gp}
                    isActive={gameData.active_player === gp.id}
                    isLeader={leaderScore > 0
                        && (gp.current_score || 0) === leaderScore}/>
            ))}
        </div>
    );
}

export default ScoresWindow;
